import { StyleDeclarationValue, css } from 'aphrodite';
import { makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IUserSessionComponentProps, UserSessionViewModelKey } from '../../../../models/AppState';
import { IEventLoggingComponentProps, withEventLogging } from '../../../../models/Logging';
import { PhoneNumberInfoViewModel } from '../../../../viewmodels/AdminViewModels';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { Modal } from '../../../../web/components/Modal';
import { TextInput } from '../../../../web/components/TextInput';
import { styleSheet } from './styles';

interface IProps extends IUserSessionComponentProps, IEventLoggingComponentProps {
	className?: string;
	requestedPhoneNumber?: string;
	onRequestClose?(): void;
	showUseButton?: boolean;
	styles?: StyleDeclarationValue[];
}

class _PhoneNumberInfoModal extends React.Component<IProps> {
	private mPhoneNumberInfoViewModel: PhoneNumberInfoViewModel;
	public constructor(props: IProps) {
		super(props);

		makeObservable<_PhoneNumberInfoModal, 'mPhoneNumberInfoViewModel'>(this, {
			mPhoneNumberInfoViewModel: observable.ref,
		});

		this.mPhoneNumberInfoViewModel = new PhoneNumberInfoViewModel(props.userSession, props.requestedPhoneNumber);
	}

	public render() {
		const { className, onRequestClose, styles } = this.props;
		return (
			<Modal
				className={`${css(styleSheet.container, ...(styles || []))} component-class ${className || ''}`}
				isOpen={true}
				onRequestClose={onRequestClose}
				useDefaultHeader={true}
			>
				<div>Phone Number Information</div>
				<div className={css(styleSheet.inputRow)}>
					<TextInput
						className={css(styleSheet.textBox)}
						disabled={this.mPhoneNumberInfoViewModel.isBusy}
						inputId='phoneNumber'
						onChange={this.onChangePhoneNumber}
						placeholder='Phone Number'
						type='text'
						value={this.mPhoneNumberInfoViewModel.phoneNumber}
					/>
					<button className='cta-button-small' onClick={this.onGo} disabled={this.mPhoneNumberInfoViewModel.isBusy}>
						Go
					</button>
				</div>
				{this.renderInfo()}
			</Modal>
		);
	}

	private renderInfo() {
		if (this.mPhoneNumberInfoViewModel.isBusy) {
			return <LoadingSpinner className={css(styleSheet.loading)} type='small' />;
		}

		if (this.mPhoneNumberInfoViewModel.errorMessage) {
			return <div className={css(styleSheet.info)}>{this.mPhoneNumberInfoViewModel.errorMessage}</div>;
		}

		const phoneInfo = this.mPhoneNumberInfoViewModel.phoneNumberInfo;
		if (phoneInfo) {
			return (
				<div>
					<div className={css(styleSheet.header)}>Phone Number</div>
					<div className={css(styleSheet.info)}>{this.mPhoneNumberInfoViewModel.phoneNumber}</div>

					<div className={css(styleSheet.header)}>Is Portable</div>
					<div className={css(styleSheet.info)}>{phoneInfo.isPortable ? 'Yes' : 'No'}</div>

					<div className={css(styleSheet.header)}>Port Type</div>
					<div className={css(styleSheet.info)}>{phoneInfo.portType}</div>

					<div className={css(styleSheet.header)}>Carrier</div>
					<div className={css(styleSheet.info)}>{phoneInfo.carrier?.name}</div>

					<div className={css(styleSheet.header)}>Rate Center Name</div>
					<div className={css(styleSheet.info)}>{phoneInfo.rateCenter?.name}</div>

					<div className={css(styleSheet.header)}>Rate Center City</div>
					<div className={css(styleSheet.info)}>{phoneInfo.rateCenter?.city}</div>

					<div className={css(styleSheet.header)}>Rate Center State</div>
					<div className={css(styleSheet.info)}>{phoneInfo.rateCenter?.state}</div>
				</div>
			);
		}
	}

	private onGo = () => {
		const { logApiError, logEvent } = this.props;

		logEvent('LoadPhoneNumberInfo');
		this.mPhoneNumberInfoViewModel.loadPhoneNumberInfo()?.catch(error => {
			logApiError('LoadPhoneNumberInfo-Error', error);
		});
	};

	private onChangePhoneNumber = (event: React.ChangeEvent<HTMLInputElement>) =>
		(this.mPhoneNumberInfoViewModel.phoneNumber = event.target.value);
}

const PhoneNumberInfoModalAsObserver = observer(_PhoneNumberInfoModal);
const PhoneNumberInfoModalWithContext = inject(UserSessionViewModelKey)(PhoneNumberInfoModalAsObserver);
export const PhoneNumberInfoModal = withEventLogging(PhoneNumberInfoModalWithContext, 'PhoneNumberInfoModal');
